import {type RefObject, useEffect, useRef} from "react";
import {Combobox} from "@/shared/ui/combobox";

interface UseLegacyComboboxOptions<T extends {id: string | number}> {
	idRef: RefObject<HTMLInputElement | null>;
	filterRef: RefObject<HTMLInputElement | null>;
	caretRef: RefObject<HTMLButtonElement | null>;
	items: T[];
	value: T | null;
	formatter: (item: T) => string;
	onValueChange: (value: T | null) => void;
	clearable?: boolean;
	disabled?: boolean;
	emptyMessage?: string;
}

/**
 * Bridges controlled React state to the legacy jQuery Combobox plugin.
 *
 * Pass `formatter`, `clearable`, and `emptyMessage` as stable references (module-level
 * constants or `useCallback`) — changes to these recreate the entire Combobox instance.
 */
export function useLegacyCombobox<T extends {id: string | number}>({
	idRef,
	filterRef,
	caretRef,
	items,
	value,
	formatter,
	onValueChange,
	clearable = false,
	disabled = false,
	emptyMessage = "",
}: UseLegacyComboboxOptions<T>): void {
	const isSyncing = useRef(false);
	const onValueChangeRef = useRef(onValueChange);
	const comboboxRef = useRef<Combobox<T> | null>(null);

	useEffect(() => {
		onValueChangeRef.current = onValueChange;
	}, [onValueChange]);

	// Creates the Combobox instance and wires the EVENT_CHANGED listener.
	// Re-runs only when stable config options change (should be rare).
	useEffect(() => {
		if (!idRef.current || !filterRef.current || !caretRef.current) return;

		const filterElement = $(filterRef.current);
		const combobox = new Combobox<T>({
			items: [],
			formatter,
			idElement: $(idRef.current),
			filterElement,
			caretElement: $(caretRef.current),
			clearable,
			emptyMessage,
		});
		comboboxRef.current = combobox;

		const handleChange = (_event: JQuery.Event, changedValue?: T) => {
			if (!isSyncing.current) {
				onValueChangeRef.current(changedValue ?? null);
			}
		};
		$(combobox).on(Combobox.EVENT_CHANGED, handleChange);

		return () => {
			$(combobox).off(Combobox.EVENT_CHANGED, handleChange);
			filterElement.typeahead("destroy");
			comboboxRef.current = null;
		};
	}, [clearable, emptyMessage, formatter]);

	// Syncs items, selected value, and disabled state to the Combobox.
	// isSyncing suppresses the resulting EVENT_CHANGED so it is not fed back as a
	// user-initiated change.

	// TODO: combine items and value as one shared state
	// {items : T[], value: T | null} | {items: undefined | null, value: null}
	// Only emit if both are here?
	useEffect(() => {
		const combobox = comboboxRef.current;
		if (!combobox) return;
		isSyncing.current = true;
		combobox.updateItems(items, value, false, true);
		// combobox.setSelectedItemById() // TODO:
		combobox.setDisabled(disabled);
		isSyncing.current = false;
	}, [items, value, disabled]);
}
